import { formatShortDay } from '../lib/format';
import type { DailyVolumeDto } from '../lib/types';
import { EmptyState } from './Feedback';

const WIDTH = 720;
const HEIGHT = 200;
const PADDING = { top: 12, right: 12, bottom: 26, left: 34 };

/**
 * Hand-rolled grouped bar chart (received / posted / failed per day). No chart library: it is a
 * plain SVG with a visually-hidden data table underneath for screen readers.
 */
export function VolumeChart({ data }: { data: DailyVolumeDto[] }): JSX.Element {
  if (data.length === 0) {
    return <EmptyState title="No volume yet" description="Documents processed will appear here." icon="▁" />;
  }

  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const maxValue = Math.max(1, ...data.flatMap((day) => [day.received, day.posted, day.failed]));
  // Round the axis up to something readable.
  const step = maxValue <= 5 ? 1 : maxValue <= 20 ? 5 : maxValue <= 50 ? 10 : Math.ceil(maxValue / 5 / 10) * 10;
  const axisMax = Math.ceil(maxValue / step) * step;
  const ticks = Array.from({ length: Math.floor(axisMax / step) + 1 }, (_, index) => index * step);

  const groupWidth = plotWidth / data.length;
  const barGap = 2;
  const barWidth = Math.max(2, (groupWidth - barGap * 4) / 3);

  const yFor = (value: number) => PADDING.top + plotHeight - (value / axisMax) * plotHeight;

  const series: { key: keyof Pick<DailyVolumeDto, 'received' | 'posted' | 'failed'>; label: string; className: string }[] = [
    { key: 'received', label: 'Received', className: 'bar-received' },
    { key: 'posted', label: 'Posted', className: 'bar-posted' },
    { key: 'failed', label: 'Failed', className: 'bar-failed' },
  ];

  // Label every day when there is room, otherwise thin them out.
  const labelStride = Math.ceil(data.length / 10);

  return (
    <figure className="chart">
      <figcaption className="chart-legend">
        {series.map((item) => (
          <span className="legend-item" key={item.key}>
            <span className={`legend-swatch ${item.className}`} aria-hidden="true" />
            {item.label}
          </span>
        ))}
      </figcaption>

      <svg
        className="chart-svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Daily document volume: received, posted and failed"
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              className="chart-gridline"
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={yFor(tick)}
              y2={yFor(tick)}
            />
            <text className="chart-axis-label" x={PADDING.left - 6} y={yFor(tick) + 3} textAnchor="end">
              {tick}
            </text>
          </g>
        ))}

        {data.map((day, index) => {
          const groupX = PADDING.left + index * groupWidth;
          return (
            <g key={day.date}>
              {series.map((item, seriesIndex) => {
                const value = day[item.key];
                const height = Math.max(value > 0 ? 1.5 : 0, (value / axisMax) * plotHeight);
                const x = groupX + barGap + seriesIndex * (barWidth + barGap);
                return (
                  <rect
                    key={item.key}
                    className={`chart-bar ${item.className}`}
                    x={x}
                    y={PADDING.top + plotHeight - height}
                    width={barWidth}
                    height={height}
                    rx={1}
                  >
                    <title>{`${formatShortDay(day.date)} — ${item.label}: ${value}`}</title>
                  </rect>
                );
              })}
              {index % labelStride === 0 ? (
                <text
                  className="chart-axis-label"
                  x={groupX + groupWidth / 2}
                  y={HEIGHT - 8}
                  textAnchor="middle"
                >
                  {formatShortDay(day.date)}
                </text>
              ) : null}
            </g>
          );
        })}

        <line
          className="chart-axis"
          x1={PADDING.left}
          x2={WIDTH - PADDING.right}
          y1={PADDING.top + plotHeight}
          y2={PADDING.top + plotHeight}
        />
      </svg>

      <table className="visually-hidden">
        <caption>Daily document volume</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Received</th>
            <th scope="col">Posted</th>
            <th scope="col">Failed</th>
          </tr>
        </thead>
        <tbody>
          {data.map((day) => (
            <tr key={day.date}>
              <th scope="row">{formatShortDay(day.date)}</th>
              <td>{day.received}</td>
              <td>{day.posted}</td>
              <td>{day.failed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
